// Chart data generation functions for Business Value Calculator

export const generateCashFlowChartData = (cashFlowReport, formData) => {
  if (!cashFlowReport || !cashFlowReport.yearwiseCashFlows) {
    return { labels: [], datasets: [] };
  }

  // Use actual year data from the report
  const years = cashFlowReport.yearwiseCashFlows.map(yearData => 
    yearData.year === 0 ? 'Year 0 (Implementation)' : `Year ${yearData.year}`
  );
  
  // Extract data from yearwiseCashFlows
  const netCashFlows = cashFlowReport.yearwiseCashFlows.map(year => year.netCashFlow);
  const cashInflows = cashFlowReport.yearwiseCashFlows.map(year => year.cashInflows);
  const cashOutflows = cashFlowReport.yearwiseCashFlows.map(year => year.cashOutflows);
  const adoptionRates = cashFlowReport.yearwiseCashFlows.map(year => year.adoptionRate);
  
  // Calculate cumulative cash flows
  const cumulativeCashFlows = [];
  let cumulative = 0;
  netCashFlows.forEach(flow => {
    cumulative += flow;
    cumulativeCashFlows.push(cumulative);
  });

  // Break-even line (y = 0)
  const breakEvenLine = Array(years.length).fill(0);

  // NPV line (if available)
  const npv = cashFlowReport.netPresentValue || 0;
  const npvLine = Array(years.length).fill(npv);

  return {
    labels: years,
    datasets: [
      {
        label: 'Net Cash Flow',
        data: netCashFlows,
        borderColor: '#A72B2A',
        backgroundColor: 'rgba(167, 43, 42, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: '#A72B2A',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      },
      {
        label: 'Cumulative Cash Flow',
        data: cumulativeCashFlows,
        borderColor: '#2E8B57',
        backgroundColor: 'rgba(46, 139, 87, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: '#2E8B57',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      },
      {
        label: 'Break-even Line',
        data: breakEvenLine,
        borderColor: '#FF6B6B',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0
      },
      {
        label: 'NPV Reference',
        data: npvLine,
        borderColor: '#4ECDC4',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [3, 3],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0
      }
    ]
  };
};

export const generateROIChartData = (cashFlowReport, formData) => {
  if (!cashFlowReport || !cashFlowReport.yearwiseCashFlows) {
    return { labels: [], datasets: [] };
  }

  // Use actual year data from the report
  const years = cashFlowReport.yearwiseCashFlows.map(yearData => 
    yearData.year === 0 ? 'Year 0 (Implementation)' : `Year ${yearData.year}`
  );
  
  // Calculate annual ROI for each year
  const annualROIs = [];
  const adoptionRateROIs = [];
  let cumulativeCashFlow = 0;
  
  cashFlowReport.yearwiseCashFlows.forEach((year, index) => {
    if (index === 0) {
      // Year 0 is implementation, no ROI
      annualROIs.push(0);
      adoptionRateROIs.push(0);
    } else {
      // Calculate ROI for this year
      const initialInvestment = parseFloat(formData.initialImplementationCost) || 0;
      cumulativeCashFlow += year.netCashFlow;
      const annualROI = initialInvestment > 0 ? (cumulativeCashFlow / initialInvestment) * 100 : 0;
      annualROIs.push(annualROI);
      
      // Calculate adoption rate ROI
      const adoptionROI = year.adoptionRate > 0 ? (year.netCashFlow / initialInvestment) * 100 : 0;
      adoptionRateROIs.push(adoptionROI);
    }
  });

  // Use actual ROI from report if available, otherwise use calculated
  const finalROI = cashFlowReport.roiYear1Percentage || 0;
  const targetROI = Math.max(20, finalROI * 0.8); // Set target based on actual ROI
  const targetROILine = Array(years.length).fill(targetROI);

  // IRR line (if available)
  const irr = cashFlowReport.internalRateOfReturn || 0;
  const irrLine = irr > 0 ? Array(years.length).fill(irr) : Array(years.length).fill(null);

  return {
    labels: years,
    datasets: [
      {
        label: 'Annual ROI (%)',
        data: annualROIs,
        borderColor: '#A72B2A',
        backgroundColor: 'rgba(167, 43, 42, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: '#A72B2A',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      },
      {
        label: 'Cumulative ROI (%)',
        data: annualROIs,
        borderColor: '#2E8B57',
        backgroundColor: 'rgba(46, 139, 87, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointBackgroundColor: '#2E8B57',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      },
      {
        label: `Target ROI (${targetROI.toFixed(1)}%)`,
        data: targetROILine,
        borderColor: '#FF6B6B',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0
      },
      {
        label: `IRR Reference (${irr.toFixed(1)}%)`,
        data: irrLine,
        borderColor: '#4ECDC4',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [3, 3],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0
      }
    ]
  };
};

export const generateSensitivityChartData = (sensitivityData, formData) => {
  if (!sensitivityData) {
    return { labels: [], yLabels: [], datasets: [] };
  }

  // Handle different sensitivity data structures
  let sensitivityAnalysis = null;
  
  if (sensitivityData.sensitivityAnalysis) {
    sensitivityAnalysis = sensitivityData.sensitivityAnalysis;
  } else if (sensitivityData.senstivityAnalysis) {
    sensitivityAnalysis = sensitivityData.senstivityAnalysis;
  } else if (Array.isArray(sensitivityData)) {
    sensitivityAnalysis = sensitivityData;
  }

  if (!sensitivityAnalysis) {
    return { labels: [], yLabels: [], datasets: [] };
  }

  // Check if we have the new structure with calculationTable
  if (sensitivityAnalysis.calculationTable && sensitivityAnalysis.calculationTable.rows && sensitivityAnalysis.calculationTable.columnHeaders) {
    const { calculationTable } = sensitivityAnalysis;
    
    // Use actual column headers from the API response
    const labels = calculationTable.columnHeaders.map(header => 
      `${calculationTable.firstParameterName || 'First Parameter'}: ${header}`
    );
    
    // Use actual row header values from the API response
    const yLabels = calculationTable.rows.map(row => 
      `${calculationTable.secondParameterName || 'Second Parameter'}: ${row.rowHeaderValue}`
    );

    // Generate data points from the actual calculation table
    const dataPoints = [];
    
    calculationTable.rows.forEach((row, rowIndex) => {
      // Left cell (first column)
      if (row.leftCell) {
        dataPoints.push({
          x: 0,
          y: rowIndex,
          roi: row.leftCell.roi || 0,
          npv: row.leftCell.npv || 0
        });
      }
      
      // Middle cell (second column)
      if (row.middleCell) {
        dataPoints.push({
          x: 1,
          y: rowIndex,
          roi: row.middleCell.roi || 0,
          npv: row.middleCell.npv || 0
        });
      }
      
      // Right cell (third column)
      if (row.rightCell) {
        dataPoints.push({
          x: 2,
          y: rowIndex,
          roi: row.rightCell.roi || 0,
          npv: row.rightCell.npv || 0
        });
      }
    });

    return {
      labels,
      yLabels,
      datasets: [
        {
          label: 'ROI Sensitivity',
          data: dataPoints,
          backgroundColor: dataPoints.map(point => {
            const roi = point.roi;
            if (roi > 50) return 'rgba(46, 139, 87, 0.8)'; // Green for high ROI
            if (roi > 20) return 'rgba(255, 193, 7, 0.8)'; // Yellow for medium ROI
            if (roi > 0) return 'rgba(255, 152, 0, 0.8)'; // Orange for low positive ROI
            return 'rgba(220, 53, 69, 0.8)'; // Red for negative ROI
          }),
          borderColor: dataPoints.map(point => {
            const roi = point.roi;
            if (roi > 50) return '#2E8B57';
            if (roi > 20) return '#FFC107';
            if (roi > 0) return '#FF9800';
            return '#DC3545';
          }),
          borderWidth: 2,
          pointRadius: 8,
          pointHoverRadius: 10
        }
      ]
    };
  }

  // Fallback to old structure for backward compatibility
  if (Array.isArray(sensitivityAnalysis)) {
    // Generate labels for cost multipliers
    const costMultipliers = [0.9, 1.0, 1.1];
    const labels = costMultipliers.map(mult => `${(mult * 100).toFixed(0)}%`);

    // Generate labels for improvement multipliers
    const improvementMultipliers = [0.9, 1.0, 1.1];
    const yLabels = improvementMultipliers.map(mult => `${(mult * 100).toFixed(0)}%`);

    // Generate data points
    const dataPoints = [];
    
    costMultipliers.forEach((costMult, costIndex) => {
      improvementMultipliers.forEach((improvementMult, improvementIndex) => {
        // Calculate ROI based on multipliers
        const baseROI = parseFloat(formData.roiYear1Percentage) || 0;
        const adjustedROI = baseROI * improvementMult / costMult;
        
        dataPoints.push({
          x: costIndex,
          y: improvementIndex,
          roi: adjustedROI
        });
      });
    });

    return {
      labels,
      yLabels,
      datasets: [
        {
          label: 'ROI Sensitivity',
          data: dataPoints,
          backgroundColor: dataPoints.map(point => {
            const roi = point.roi;
            if (roi > 50) return 'rgba(46, 139, 87, 0.8)'; // Green for high ROI
            if (roi > 20) return 'rgba(255, 193, 7, 0.8)'; // Yellow for medium ROI
            if (roi > 0) return 'rgba(255, 152, 0, 0.8)'; // Orange for low positive ROI
            return 'rgba(220, 53, 69, 0.8)'; // Red for negative ROI
          }),
          borderColor: dataPoints.map(point => {
            const roi = point.roi;
            if (roi > 50) return '#2E8B57';
            if (roi > 20) return '#FFC107';
            if (roi > 0) return '#FF9800';
            return '#DC3545';
          }),
          borderWidth: 2,
          pointRadius: 8,
          pointHoverRadius: 10
        }
      ]
    };
  }

  return { labels: [], yLabels: [], datasets: [] };
};
